/**
 * @fileoverview Componente ConversationItemMC optimizado para renderizar un ítem de conversación en el Inbox.
 * Implementa arquitectura Atomic Design (molecule), separación estricta de lógica UI y negocio, y optimización de performance con useMemo/useCallback.
 * @module ConversationItemMC
 * @category Component - Molecule
 * @since 3.1.0
 * @version 3.1.0
 *
 * @typedef {Object} ConversationItemProps
 * @property {Object} conversation - Objeto de conversación con datos principales y etiquetas
 * @property {boolean} isSelected - Indica si el ítem está seleccionado
 * @property {function} onSelect - Handler para seleccionar la conversación
 * @property {boolean} isChecked - Estado del checkbox de selección múltiple
 * @property {function} onToggle - Handler para alternar el checkbox
 * @property {boolean} isSelectingConversations - Si está en modo selección múltiple
 *
 * @example
 * <ConversationItemMC
 *   conversation={conversation}
 *   isSelected={isSelected}
 *   onSelect={handleSelect}
 *   isChecked={isChecked}
 *   onToggle={handleToggle}
 *   isSelectingConversations={isSelecting}
 * />
 *
 * @see {@link ConversationAvatarMC} Componente de avatar de conversación
 * @see {@link ConversationTagsMC} Componente de tags de conversación
 * @see {@link ConversationFeatureIconsMC} Componente de iconos de features
 * @see {@link useConversationItemTimeUI} Hook para formateo de tiempo
 * @see InboxMC.json Archivo de traducciones
 */
import React, {
  memo,
  useState,
  useMemo,
  useCallback
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckedIconMC,
  ConversationAvatarMC,
  ConversationTagsMC,
  ConversationFeatureIconsMC,
  FepBadgeMC
} from './components/index';
import LabelMC from '../../components/LabelMC';
import useConversationItemTimeUI from '../../hooks/inbox/ui/useConversationItemTimeUI';
import { getTemperatureKey, getTemperatureLevelFromScore } from './utils/leadScoreUtils';

/**
 * Componente optimizado para renderizar un ítem de conversación en el Inbox.
 *
 * @param {ConversationItemProps} props - Propiedades del componente
 * @returns {JSX.Element} Elemento JSX renderizado
 */
const ConversationItemMC = memo(({
  conversation,
  isSelected,
  onSelect,
  isChecked,
  onToggle,
  isSelectingConversations
}) => {
  const { t } = useTranslation('InboxMC');
  const howLongAgo = useConversationItemTimeUI();
  const [isHovered, setIsHovered] = useState(false);

  // Memoización de clases base y hover
  const baseClasses = useMemo(() => 'tw-flex tw-items-start tw-p-2 tw-rounded-lg tw-bg-white', []);
  const containerClass = useMemo(
    () => (isSelected
      ? `${baseClasses} !tw-bg-blue-50 tw-border-l-4 tw-border-blue-300`
      : `${baseClasses} tw-transition-colors`),
    [isSelected, baseClasses]
  );
  const hoverStyle = useMemo(
    () => (!isSelected && isHovered ? { backgroundColor: '#eee' } : {}),
    [isSelected, isHovered]
  );

  // Valores para el avatar derivados de la conversación (detectan cambios en status_chat / serialized)
  const avatarProps = useMemo(() => ({
    ticketStatus: conversation.ticketStatus,
    statusChat: conversation.statusChat,
    platform: conversation.platform
  }), [conversation]);

  // Handlers optimizados con useCallback
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);
  const handleClick = useCallback(() => onSelect(conversation), [onSelect, conversation]);

  // Memoización de labels de features
  const featureLabels = useMemo(() => [
    conversation.humanHelpLabel && (
      <LabelMC
        key="humanHelp"
        label={t('conversation.human_help.label')}
        iconName="hand-outline"
        iconClassName="tw-ml-1"
        iconSize="xs"
        className="!tw-bg-violet-200 !tw-text-violet-700"
      />
    ),
    conversation.buyIntentionLabel && (
      <LabelMC
        key="buyIntention"
        label={t('conversation.buy_intention.label')}
        iconName="shopping-bag-modern-outline"
        iconClassName="tw-ml-1"
        iconSize="xs"
        className="!tw-bg-[#EAFEFF] !tw-text-[#03749B]"
      />
    ),
    conversation.generatedMiaOrderLabel && (
      <LabelMC
        key="generatedOrder"
        label={t('conversation.generated_order.label')}
        iconName="shopping-bag-modern-outline"
        iconClassName="tw-ml-1"
        iconSize="xs"
        className="!tw-bg-green-50 !tw-text-green-700"
      />
    ),
    conversation.yourTurnLabel && (
      <LabelMC
        key="yourTurn"
        label={t('conversation.your_turn.label')}
        className="!tw-bg-yellow-300 !tw-text-yellow-800"
      />
    ),
    conversation.aiCopilotsEnabled && conversation.leadScore != null
      && getTemperatureLevelFromScore(conversation.leadScore) === 'very_high' && (
      <LabelMC
        key="leadTemperature"
        label={t(getTemperatureKey('very_high'))}
        iconName="thermometer-outline"
        iconClassName="tw-ml-1"
        iconSize="xs"
        className="!tw-bg-red-50 !tw-text-red-600"
      />
    ),
    conversation.aiCopilotsEnabled && conversation.leadScore != null
      && getTemperatureLevelFromScore(conversation.leadScore) === 'high' && (
      <LabelMC
        key="leadTemperature"
        label={t(getTemperatureKey('high'))}
        iconName="thermometer-outline"
        iconClassName="tw-ml-1"
        iconSize="xs"
        className="!tw-bg-orange-50 !tw-text-orange-600"
      />
    ),
    conversation.aiCopilotsEnabled && conversation.leadScore != null
      && getTemperatureLevelFromScore(conversation.leadScore) === 'medium' && (
      <LabelMC
        key="leadTemperature"
        label={t(getTemperatureKey('medium'))}
        iconName="thermometer-outline"
        iconClassName="tw-ml-1"
        iconSize="xs"
        className="!tw-bg-yellow-50 !tw-text-yellow-600"
      />
    ),
    conversation.aiCopilotsEnabled && conversation.leadScore != null
      && getTemperatureLevelFromScore(conversation.leadScore) === 'low' && (
      <LabelMC
        key="leadTemperature"
        label={t(getTemperatureKey('low'))}
        iconName="thermometer-outline"
        iconClassName="tw-ml-1"
        iconSize="xs"
        className="!tw-bg-cyan-50 !tw-text-cyan-600"
      />
    ),
    conversation.aiCopilotsEnabled && conversation.leadScore != null
      && getTemperatureLevelFromScore(conversation.leadScore) === 'very_low' && (
      <LabelMC
        key="leadTemperature"
        label={t(getTemperatureKey('very_low'))}
        iconName="thermometer-outline"
        iconClassName="tw-ml-1"
        iconSize="xs"
        className="!tw-bg-blue-50 !tw-text-blue-600"
      />
    )
  ].filter(Boolean), [
    conversation.humanHelpLabel,
    conversation.buyIntentionLabel,
    conversation.generatedMiaOrderLabel,
    conversation.yourTurnLabel,
    conversation.aiCopilotsEnabled,
    conversation.leadScore,
    t
  ]);

  // Memoización del nombre del cliente
  const customerName = useMemo(() => conversation.fullName || '', [conversation.fullName]);

  // Memoización del agente asignado
  const assignedAgentName = useMemo(() => conversation.assignedAgent?.fullName || t('chat.unassigned'), [conversation.assignedAgent?.fullName, t]);

  // Memoización del color del timestamp
  const timeColorClass = useMemo(
    () => (conversation.isUnRead ? 'tw-text-blue-500' : 'tw-text-gray-500'),
    [conversation.isUnRead]
  );

  // Memoización del contador de no leídos
  const unreadBadge = useMemo(
    () => (conversation.isUnRead ? (
      <div className="tw-flex tw-items-center tw-justify-center tw-min-w-5 tw-h-5 tw-bg-red-500 tw-rounded-full tw-ml-1">
        <span className="tw-text-xs tw-font-semibold tw-text-white">
          {conversation.unreadCount || ''}
        </span>
      </div>
    ) : null),
    [conversation.isUnRead, conversation.unreadCount]
  );

  return (
    <div
      className={`${containerClass} tw-cursor-pointer tw-w-full tw-overflow-hidden tw-max-w-full`}
      style={hoverStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      tabIndex={0}
      role="button"
      aria-pressed={isSelected}
    >
      {isSelectingConversations && (
        <div className="tw-flex-shrink-0 tw-flex tw-items-center tw-mr-0.5">
          <CheckedIconMC
            checked={isChecked}
            onToggle={onToggle}
          />
        </div>
      )}
      <div className="tw-w-full tw-mb-1 tw-flex tw-flex-col tw-gap-1">
        <div className="tw-flex tw-flex-wrap tw-w-full tw-gap-1 tw-justify-end">
          {featureLabels}
        </div>
        <div className="tw-flex tw-w-full tw-gap-2 tw-min-w-0">
          <div className="tw-flex-shrink-0">
            <ConversationAvatarMC
              ticketStatus={avatarProps.ticketStatus}
              statusChat={avatarProps.statusChat}
              platform={avatarProps.platform}
            />
          </div>
          <div className="tw-flex-1 tw-min-w-0 tw-max-w-full">
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-1 tw-min-w-0 tw-max-w-full">
              <div
                className="tw-flex-1 tw-min-w-0 tw-flex tw-flex-col"
                style={{ width: 0, flexGrow: 1 }}
              >
                <span className="tw-font-semibold tw-text-default tw-text-gray-950 tw-truncate tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
                  {customerName}
                </span>
                {conversation.platformRetailerName && (
                  <span className="tw-text-[11px] tw-leading-none tw-mt-0.5 tw-font-medium tw-text-gray-500 tw-truncate tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
                    {conversation.platformRetailerName}
                  </span>
                )}
              </div>
              <span
                className={`tw-text-xs tw-font-normal tw-ml-1 ${timeColorClass} tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-min-w-0 tw-max-w-full`}
                style={{ flexBasis: 'auto' }}
              >
                {howLongAgo(conversation.lastMessageAt)}
              </span>
            </div>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-1 tw-min-w-0 tw-max-w-full">
              <span className="tw-text-xs tw-font-normal tw-text-gray-500 tw-truncate tw-flex-1 tw-min-w-0 tw-max-w-full">
                {assignedAgentName}
              </span>
              {unreadBadge}
            </div>
            <div className="tw-min-w-0 tw-max-w-full tw-flex tw-flex-col tw-gap-1">
              <ConversationFeatureIconsMC conversation={conversation} />
              <ConversationTagsMC tags={conversation.tags} />
              <FepBadgeMC conversation={conversation} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ConversationItemMC.displayName = 'ConversationItemMC';

export default ConversationItemMC;
